import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface HeroGalleryProps {
  images: Array<{
    src: string;
    alt: string;
    caption?: string;
  }>;
  autoAdvanceInterval?: number; // milliseconds
  className?: string;
}

export function HeroGallery({ 
  images, 
  autoAdvanceInterval = 5000, // 5 seconds default
  className = "" 
}: HeroGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToImage = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Auto-advance functionality
  useEffect(() => {
    if (!isAutoPlaying || images.length <= 1) return;

    const interval = setInterval(nextImage, autoAdvanceInterval);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextImage, autoAdvanceInterval, images.length]);

  // Pause auto-advance when user hovers over controls
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  if (!images || images.length === 0) {
    return <div className="min-h-[400px] bg-gray-200 flex items-center justify-center">No images available</div>;
  }

  const currentImage = images[currentIndex];

  return (
    <div className={`relative w-full ${className}`}>
      {/* Main Image Display */}
      <div className="relative overflow-hidden rounded-lg h-[400px] md:h-[500px]">
        <div className="absolute -inset-4 rounded-full bg-secondary/30 blur-xl z-0"></div>
        <img
          src={currentImage.src}
          alt={currentImage.alt}
          className="relative z-10 w-full h-full object-cover rounded-lg shadow-lg transform hover:scale-[1.02] transition-transform duration-500 ease-in-out"
        />
        
        {/* Navigation Controls */}
        {images.length > 1 && (
          <>
            {/* Previous Button */}
            <button
              onClick={prevImage}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-primary rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="Previous image"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Next Button */}
            <button
              onClick={nextImage}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-primary rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="Next image"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {images.length > 1 && (
          <div 
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentIndex
                    ? 'bg-white shadow-md scale-110'
                    : 'bg-white/60 hover:bg-white/80'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute top-4 right-4 z-20 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-medium">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Caption */}
      {currentImage.caption && (
        <div className="mt-3 text-center">
          <p className="text-sm text-foreground/70 italic">{currentImage.caption}</p>
        </div>
      )}

      {/* Auto-play indicator */}
      {isAutoPlaying && images.length > 1 && (
        <div className="absolute top-4 left-4 z-20 bg-black/50 text-white px-2 py-1 rounded text-xs flex items-center">
          <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
          Auto
        </div>
      )}
    </div>
  );
}